/**
 * Composant Matomo Analytics - Zone prête pour le tracking
 * 
 * INSTALLATION MATOMO SUR VOTRE VPS:
 * ----------------------------------
 * 1. Installer les prérequis (Ubuntu/Debian):
 *    sudo apt update && sudo apt install -y apache2 mysql-server php php-mysql php-xml php-curl php-gd php-mbstring unzip
 * 
 * 2. Créer la base de données MySQL:
 *    sudo mysql -u root -p
 *    CREATE DATABASE matomo;
 *    CREATE USER 'matomo'@'localhost' IDENTIFIED BY 'votre_mot_de_passe';
 *    GRANT ALL ON matomo.* TO 'matomo'@'localhost';
 *    FLUSH PRIVILEGES;
 *    EXIT;
 * 
 * 3. Télécharger et installer Matomo:
 *    cd /var/www/html
 *    sudo wget https://builds.matomo.org/matomo-latest.zip
 *    sudo unzip matomo-latest.zip
 *    sudo chown -R www-data:www-data matomo
 *    sudo chmod -R 755 matomo
 * 
 * 4. Configurer Apache (optionnel si vous voulez un sous-domaine):
 *    sudo nano /etc/apache2/sites-available/matomo.conf
 *    # Ajouter la config VirtualHost
 *    sudo a2ensite matomo.conf
 *    sudo systemctl reload apache2
 * 
 * 5. Finaliser l'installation:
 *    Accéder à http://votre-vps.com/matomo ou http://analytics.asarcsg.com
 *    Suivre l'assistant d'installation web
 * 
 * 6. Récupérer le code de tracking:
 *    Dans Matomo: Administration > Sites web > Code de suivi
 *    Copier l'URL et le Site ID
 * 
 * 7. Configurer ce composant:
 *    Ajouter dans frontend/.env:
 *    REACT_APP_MATOMO_URL=https://analytics.asar-csg.com
 *    REACT_APP_MATOMO_SITE_ID=1
 * 
 * MODE SANS COOKIES (RGPD):
 * -------------------------
 * Matomo peut fonctionner sans cookies. Dans Matomo:
 * Administration > Confidentialité > Anonymiser les données
 * Cocher "Forcer le suivi sans cookies"
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Récupérer la config depuis les variables d'environnement
const MATOMO_URL = process.env.REACT_APP_MATOMO_URL;
const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID;

/**
 * Hook pour tracker les pages vues
 */
export const useMatomoPageView = () => {
  const location = useLocation();
  
  useEffect(() => {
    if (!MATOMO_URL || !MATOMO_SITE_ID) return;
    
    // Tracker la page vue
    if (window._paq) {
      window._paq.push(['setCustomUrl', window.location.href]);
      window._paq.push(['setDocumentTitle', document.title]);
      window._paq.push(['trackPageView']);
    }
  }, [location.pathname]);
};

/**
 * Fonction pour tracker un événement personnalisé
 * @param {string} category - Catégorie de l'événement (ex: "Formulaire")
 * @param {string} action - Action (ex: "Soumission")
 * @param {string} name - Nom optionnel (ex: "Demande devis auto")
 * @param {number} value - Valeur optionnelle
 */
export const trackEvent = (category, action, name = '', value = null) => {
  if (!MATOMO_URL || !MATOMO_SITE_ID) return;
  
  if (window._paq) {
    const eventData = ['trackEvent', category, action];
    if (name) eventData.push(name);
    if (value !== null) eventData.push(value);
    window._paq.push(eventData);
  }
};

/**
 * Fonction pour tracker une recherche
 * @param {string} keyword - Mot-clé recherché
 * @param {string} category - Catégorie (optionnel)
 * @param {number} resultsCount - Nombre de résultats
 */
export const trackSearch = (keyword, category = '', resultsCount = 0) => {
  if (!MATOMO_URL || !MATOMO_SITE_ID) return;
  
  if (window._paq) {
    window._paq.push(['trackSiteSearch', keyword, category, resultsCount]);
  }
};

/**
 * Fonction pour tracker un téléchargement
 * @param {string} url - URL du fichier téléchargé
 */
export const trackDownload = (url) => {
  if (!MATOMO_URL || !MATOMO_SITE_ID) return;
  
  if (window._paq) {
    window._paq.push(['trackLink', url, 'download']);
  }
};

/**
 * Composant principal Matomo - À placer dans App.js
 * Injecte le script de tracking si configuré
 */
const MatomoTracker = () => {
  useEffect(() => {
    // Ne rien faire si pas configuré
    if (!MATOMO_URL || !MATOMO_SITE_ID) {
      return;
    }
    
    // Vérifier si déjà initialisé
    if (window._paq) return;
    
    // Initialiser Matomo
    window._paq = window._paq || [];
    
    // Configuration recommandée pour RGPD
    window._paq.push(['disableCookies']); // Mode sans cookies par défaut
    window._paq.push(['setDoNotTrack', true]); // Respecter Do Not Track
    window._paq.push(['trackPageView']);
    window._paq.push(['enableLinkTracking']);
    
    // Injecter le script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `${MATOMO_URL}/matomo.js`;
    
    // Configuration du tracker
    window._paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
    window._paq.push(['setSiteId', MATOMO_SITE_ID]);
    
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);
    
    
  }, []);
  
  // Tracker les changements de page
  useMatomoPageView();
  
  return null;
};

export default MatomoTracker;
